import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Calendar, CheckCircle, AlertCircle, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface CalendlyUser {
  name: string;
  email: string;
  scheduling_url: string;
}

interface CalendlyEventType {
  uri: string;
  name: string;
  duration: number;
  active: boolean;
}

interface ConnectionStatus {
  connected: boolean;
  user?: CalendlyUser;
  eventTypes?: CalendlyEventType[];
  error?: string;
  checkedAt?: string;
}

export const CalendlyConnect = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const checkConnection = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('calendly-api', {
        body: { action: 'get-event-types' }
      });

      if (error) throw error;

      if (data.success) {
        setStatus({
          connected: true,
          user: data.user,
          eventTypes: data.eventTypes,
          checkedAt: new Date().toISOString(),
        });
      } else {
        setStatus({
          connected: false,
          error: data.error || 'Connection failed',
          checkedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error checking Calendly connection:', error);
      setStatus({
        connected: false,
        error: error instanceof Error ? error.message : 'Connection check failed',
        checkedAt: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await checkConnection();
    toast({
      title: 'Connection Refreshed',
      description: status?.connected ? 'Calendly is connected and working' : 'Please check your API key configuration',
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/30">
            <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base">Calendly</CardTitle>
            <CardDescription className="text-xs">
              Scheduling & availability management
            </CardDescription>
          </div>
          {status?.connected ? (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <CheckCircle className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          ) : (
            <Badge variant="destructive">
              <AlertCircle className="h-3 w-3 mr-1" />
              Not Connected
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {status?.connected ? (
          <>
            <div className="bg-muted/50 rounded-lg p-3 space-y-2">
              {status.user && (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Account:</span>
                    <span className="font-medium">{status.user.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Email:</span>
                    <span className="font-medium">{status.user.email}</span>
                  </div>
                </>
              )}
              {status.eventTypes && status.eventTypes.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground mb-1">Active Event Types:</p>
                  <div className="flex flex-wrap gap-1">
                    {status.eventTypes.slice(0, 3).map((et) => (
                      <Badge key={et.uri} variant="secondary" className="text-xs">
                        {et.name} ({et.duration}min)
                      </Badge>
                    ))}
                    {status.eventTypes.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{status.eventTypes.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              {status.checkedAt && (
                <div className="flex items-center gap-2 text-sm pt-2 border-t">
                  <span className="text-muted-foreground">Last checked:</span>
                  <span>{formatDistanceToNow(new Date(status.checkedAt), { addSuffix: true })}</span>
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex-1"
              >
                {refreshing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Status
                  </>
                )}
              </Button>
              
              {status.user?.scheduling_url && (
                <Button
                  variant="outline"
                  onClick={() => window.open(status.user?.scheduling_url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-700 dark:text-red-300">
                {status?.error || 'Calendly API key not configured or invalid'}
              </p>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                To connect Calendly:
              </p>
              <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                <li>Go to Calendly → Settings → Integrations → API & Webhooks</li>
                <li>Generate a Personal Access Token</li>
                <li>Add it as CALENDLY_API_KEY secret</li>
              </ol>
            </div>
            
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-full"
            >
              {refreshing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Check Connection
                </>
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendlyConnect;
